import {IconButton} from '@dropbox/dig-components/buttons';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {atoms} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {NotificationFill, NotificationLine} from '@dropbox/dig-icons/assets';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {
  EmployeeEventSubscriptionEventType,
  EmployeeEventSubscriptionState,
  EmployeeService,
} from 'client';
import {reportAndLogError} from 'helpers/logging';
import {t} from 'i18next';
import {getService} from 'utilities';

type ProfileNotificationToggleProps = {
  ldap: string;
  subscription: EmployeeEventSubscriptionState;
};

export const ProfileNotificationToggle = ({ldap, subscription}: ProfileNotificationToggleProps) => {
  const queryClient = useQueryClient();
  const eventType = subscription.event_type;
  const isOOO = eventType === EmployeeEventSubscriptionEventType.OOO;
  const isLocked = subscription.is_locked && !isOOO;
  const isActive = isLocked || subscription.is_subscribed;

  const mutation = useMutation({
    mutationFn: () =>
      subscription.is_subscribed
        ? getService(
            EmployeeService
          ).unsubscribeFromEmployeeEventApiV1PeopleUserIdEventSubscriptionsEventTypeDelete(
            ldap,
            eventType
          )
        : getService(
            EmployeeService
          ).subscribeToEmployeeEventApiV1PeopleUserIdEventSubscriptionsEventTypePut(
            ldap,
            eventType
          ),
    onSuccess: () => {
      void queryClient.invalidateQueries({queryKey: ['profile', ldap]});
      void queryClient.invalidateQueries({queryKey: ['home', 'people-calendar']});
      void queryClient.invalidateQueries({queryKey: ['notifications', 'upcoming']});
    },
    onError: (error) => {
      reportAndLogError(error, 'Error updating profile event notification subscription');
    },
  });

  const tooltipTitle = getTooltipTitle(subscription);
  const ariaLabel = getAriaLabel(subscription);

  return (
    <Tooltip title={tooltipTitle} placement="bottom">
      <span>
        <IconButton
          aria-label={ariaLabel}
          size="small"
          variant="borderless"
          disabled={isLocked || mutation.isPending}
          onClick={() => {
            analyticsLogger().logEvent('PROFILE_INTERACTION', {
              action: subscription.is_subscribed ? 'unsubscribe' : 'subscribe',
              source: eventType,
            });
            mutation.mutate();
          }}
        >
          <UIIcon
            src={isActive ? NotificationFill : NotificationLine}
            className={atoms({color: isActive ? 'Text Base' : 'Text Subtle'})}
          />
        </IconButton>
      </span>
    </Tooltip>
  );
};

const getTooltipTitle = (subscription: EmployeeEventSubscriptionState) => {
  if (
    subscription.is_locked &&
    subscription.event_type !== EmployeeEventSubscriptionEventType.OOO
  ) {
    return t('profile_notify_locked_birthday');
  }

  if (subscription.event_type === EmployeeEventSubscriptionEventType.OOO) {
    return subscription.is_subscribed
      ? t('profile_notify_unsubscribe_ooo')
      : t('profile_notify_subscribe_ooo');
  }

  return subscription.is_subscribed
    ? t('profile_notify_unsubscribe_birthday')
    : t('profile_notify_subscribe_birthday');
};

const getAriaLabel = (subscription: EmployeeEventSubscriptionState) => {
  if (subscription.event_type === EmployeeEventSubscriptionEventType.OOO) {
    return subscription.is_subscribed
      ? t('profile_notify_unsubscribe_ooo')
      : t('profile_notify_subscribe_ooo');
  }

  return subscription.is_subscribed
    ? t('profile_notify_unsubscribe_birthday')
    : t('profile_notify_subscribe_birthday');
};
